import ArrowOutwardOutlinedIcon from "@mui/icons-material/ArrowOutwardOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { fillRouteWithSlugs } from "@utils";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import ApprovalPinModal from "src/components/DocViewer/components/docInfoDrawer/approversTab/pin/ApprovalPinModal";
import { ReadOnlyQmsDocumentViewer } from "src/components/DocViewer/components/readOnly/ReadOnlyQmsDocumentViewer";
import { ProgressBubbleBar } from "src/components/Form/ProgressBubbleBar";
import { ROUTE_SLUGS, ROUTES } from "src/config/navigation/routes";
import { useGetDocumentWithAllVersions } from "src/hooks/queries";
import {
  useMarkTrainingTaskRead,
  useSignTrainingTask,
} from "src/hooks/queries/task-state-mutations";
import type { UnifiedTask } from "src/services/backend";
import type { User } from "src/stores/models";
import { getPreviousVersion } from "src/utils/documents";
import { TrainingQuizStep } from "./TrainingQuizStep";

type TrainingStep = "document" | "quiz";

interface TrainingDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: UnifiedTask;
  user: User;
  orgId: string;
  documentDeviceId?: string | null;
}

export const TrainingDocumentModal = ({
  isOpen,
  onClose,
  task,
  user,
  orgId,
  documentDeviceId,
}: TrainingDocumentModalProps) => {
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [activeStep, setActiveStep] = useState<TrainingStep>("document");
  const [readCompleted, setReadCompleted] = useState(false);

  const markTrainingRead = useMarkTrainingTaskRead({ orgId });
  const signTraining = useSignTrainingTask({ orgId });

  const { data: document, isLoading: isLoadingDocument } =
    useGetDocumentWithAllVersions(orgId, task.documentId ?? "");

  const selectedVersion = useMemo(() => {
    if (!document) return null;
    if (!task.documentVersionIdToTrainOn) return null;
    return (
      document.versions.find((v) => v.id === task.documentVersionIdToTrainOn) ??
      null
    );
  }, [document, task.documentVersionIdToTrainOn]);

  const previousVersion = useMemo(() => {
    if (!document || !selectedVersion) return null;
    return getPreviousVersion(selectedVersion, document);
  }, [document, selectedVersion]);

  const isAssignee = task.assignee?.id === user.id;
  const hasQuiz = !!task.trainingQuizId;
  const hasReadDocument = !!task.trainingReadAt || readCompleted;
  const isSigned = !!task.trainingSignedAt;
  const canCompleteTraining =
    !isLoadingDocument && !!document && !!selectedVersion;

  useEffect(() => {
    setHasAcknowledged(false);
  }, [isOpen, selectedVersion?.id]);

  useEffect(() => {
    setReadCompleted(isOpen && !!task.trainingReadAt);
  }, [isOpen, task.id, task.trainingReadAt]);

  useEffect(() => {
    if (!isOpen) {
      setActiveStep("document");
      return;
    }

    if (hasQuiz && hasReadDocument && canCompleteTraining) {
      setActiveStep("quiz");
    }
  }, [canCompleteTraining, hasQuiz, hasReadDocument, isOpen, task.id]);

  const handleMarkRead = async () => {
    if (!canCompleteTraining) return;
    await markTrainingRead.mutateAsync({ stateId: task.id });
    setReadCompleted(true);
    if (hasQuiz) {
      setActiveStep("quiz");
    } else {
      setPinModalOpen(true);
    }
  };

  const handleSign = async (pin?: string) => {
    if (!pin || !canCompleteTraining) return;
    await signTraining.mutateAsync({ stateId: task.id, pin });
    setPinModalOpen(false);
    onClose();
  };

  const linkedDocumentDeviceId = document?.deviceId ?? documentDeviceId;
  const linkedDocumentHref =
    document && linkedDocumentDeviceId
      ? `${fillRouteWithSlugs(ROUTES.QMS_OPEN_DOC, {
          [ROUTE_SLUGS.ORG_ID]: orgId,
          [ROUTE_SLUGS.DEVICE_ID]: linkedDocumentDeviceId,
          [ROUTE_SLUGS.DOC_ID]: document.id,
        })}${selectedVersion?.id ? `?v=${selectedVersion.id}` : ""}`
      : null;

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: { height: "90vh", display: "flex", flexDirection: "column" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            px: 3,
            py: 2,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap>
              {task.title}
            </Typography>
            {(hasQuiz || linkedDocumentHref) && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1.5,
                  mt: 0.5,
                }}
              >
                {hasQuiz && (
                  <ProgressBubbleBar
                    ariaLabel="Training progress"
                    steps={[
                      {
                        id: "training-document",
                        label: "Read document",
                        status:
                          activeStep === "document"
                            ? "active"
                            : hasReadDocument
                              ? "completed"
                              : "pending",
                      },
                      {
                        id: "training-quiz",
                        label: "Take quiz",
                        status:
                          activeStep === "quiz"
                            ? "active"
                            : task.trainingQuizPassedAt
                              ? "completed"
                              : "pending",
                      },
                    ]}
                  />
                )}
                {linkedDocumentHref && (
                  <Button
                    component={RouterLink}
                    to={linkedDocumentHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    color="inherit"
                    startIcon={<ArrowOutwardOutlinedIcon fontSize="small" />}
                    sx={{ px: 1.25 }}
                  >
                    Go to document
                  </Button>
                )}
              </Box>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label="Close training modal">
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            p: 0,
          }}
        >
          <Box
            sx={{
              display: activeStep === "document" ? "flex" : "none",
              minHeight: 0,
              flex: 1,
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            {isLoadingDocument && (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            )}

            {!isLoadingDocument && !selectedVersion && (
              <Alert severity="error" sx={{ m: 3 }}>
                Could not load the training document version. It may have been
                deleted.
              </Alert>
            )}

            {!isLoadingDocument && document && selectedVersion && (
              <ReadOnlyQmsDocumentViewer
                orgId={orgId}
                document={document}
                selectedVersion={selectedVersion}
                previousVersion={previousVersion}
              />
            )}
          </Box>

          {hasQuiz && hasReadDocument && (
            <Box
              sx={{
                display: activeStep === "quiz" ? "flex" : "none",
                minHeight: 0,
                flex: 1,
              }}
            >
              <TrainingQuizStep
                orgId={orgId}
                taskStateId={task.id}
                user={user}
                onComplete={onClose}
                onReviewDocument={() => setActiveStep("document")}
              />
            </Box>
          )}
        </DialogContent>

        {activeStep === "document" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              px: 3,
              py: 2,
              borderTop: 1,
              borderColor: "divider",
            }}
          >
            {!isAssignee && (
              <Alert severity="info" sx={{ flex: 1 }}>
                Only{" "}
                {task.assignee?.firstName ??
                  task.assignee?.email ??
                  "the assignee"}{" "}
                can complete this training.
              </Alert>
            )}

            {isAssignee && isSigned && (
              <Chip
                color="success"
                label={`Signed ${dayjs(task.trainingSignedAt).format(
                  "MMM D, YYYY h:mm A"
                )}`}
              />
            )}

            {isAssignee &&
              canCompleteTraining &&
              !isSigned &&
              !hasReadDocument && (
                <>
                  <Box sx={{ flex: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={hasAcknowledged}
                          onChange={(event) =>
                            setHasAcknowledged(event.target.checked)
                          }
                        />
                      }
                      label="I have read the entire document"
                    />
                  </Box>
                  <Tooltip
                    title={
                      hasAcknowledged
                        ? ""
                        : "Acknowledge that you have read the document before marking as read"
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        onClick={handleMarkRead}
                        disabled={
                          !hasAcknowledged || markTrainingRead.isPending
                        }
                      >
                        Mark as read
                      </Button>
                    </span>
                  </Tooltip>
                </>
              )}

            {isAssignee &&
              canCompleteTraining &&
              hasQuiz &&
              hasReadDocument &&
              !isSigned && (
                <>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {task.trainingQuizPassedAt
                        ? "Quiz passed — continue to sign the training"
                        : "Document read — take the quiz to continue"}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => setActiveStep("quiz")}
                  >
                    {task.trainingQuizPassedAt
                      ? "Continue to sign"
                      : "Take quiz"}
                  </Button>
                </>
              )}

            {isAssignee &&
              canCompleteTraining &&
              hasReadDocument &&
              !hasQuiz &&
              !isSigned && (
                <>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Ready to sign — enter your eCFR pin to complete
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => setPinModalOpen(true)}
                    disabled={signTraining.isPending}
                  >
                    Sign training
                  </Button>
                </>
              )}
          </Box>
        )}
      </Dialog>

      {!hasQuiz && (
        <ApprovalPinModal
          isOpen={pinModalOpen}
          onClose={() => setPinModalOpen(false)}
          onApprove={handleSign}
          user={user}
          title="Sign Training"
          description="Enter your 4-digit PIN to certify that you've read and understood the linked document."
          saveText="Sign"
          confirmationWarningText="By pressing Sign below, you certify that you have read and understood the linked document."
        />
      )}
    </>
  );
};

export default TrainingDocumentModal;
